import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Howl } from 'howler';
import _ from 'lodash';
import { combineLatest } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AcdTypeEnum } from 'src/app/samwebDescription/samwin/AcdTypeEnum';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';
import { SamwinCallListSelector } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.selector';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinLinesState } from 'src/app/store/samwin/samwin-lines/samwin-lines.state';
import { SamwinMessageLinesState } from 'src/app/store/samwin/samwin-message-lines/samwin-message-lines.state';
import * as ringingWav from '../../../assets/audio/ringing.wav';
import * as signalWav from '../../../assets/audio/signalton.wav';
import { AudioService } from './audio.service';

@Injectable({
   providedIn: 'root',
})
export class RingerService {
   private primaryHowls: Set<Howl> = new Set<Howl>();
   private secondaryHowls: Set<Howl> = new Set<Howl>();

   private isRinging = false;

   constructor(private store: Store, private audioService: AudioService) {}

   public initializeRingerService() {
      const isPhoneRinging$ = this.store.select(SamwinLinesStateSelector.isPhoneRinging);
      const isPhoneConnected$ = this.store.select(SamwinLinesState.isSourceLineConnected);

      const isMessageAssigned$ = this.store.select(SamwinMessageLinesState.isMessageAssigned);
      const isMessageConnected$ = this.store.select(SamwinMessageLinesState.isMessageConnected);

      combineLatest([isPhoneRinging$, isPhoneConnected$, isMessageAssigned$, isMessageConnected$])
         .pipe(
            tap(([isPhoneRinging, isPhoneConnected, isMessageAssigned, isMessageConnected]) => {
               // Ignore message connected state when phone is ringing.
               if (isPhoneConnected || (isMessageConnected && !isPhoneRinging)) {
                  this.phoneRingingChanged(false);
                  return;
               }
               if (this.isRinging && (isPhoneRinging || isMessageAssigned)) {
                  return;
               }
               this.phoneRingingChanged(isPhoneRinging || isMessageAssigned);
            })
         )
         .subscribe();

      this.audioService.chosenRingerDevices$.pipe(tap((ringerDevices) => this.setupRingtonePlayers(ringerDevices))).subscribe();
   }

   private phoneRingingChanged(isRinging: boolean) {
      this.isRinging = isRinging;

      if (isRinging) {
         const isCurrentCallInASharedSkill = this.store.selectSnapshot(SamwinCallListSelector.getActiveOrRingingCallFromSharedSkill);
         const currentAcdExtensionLineType = this.store.selectSnapshot(SamwinAcdExtensionStateSelectors.getCurrentAcdExtensionLineType);
         const isRingtoneUsageDisabled = this.store.selectSnapshot(SamwinAcdExtensionStateSelectors.ringtoneUsageDisabled);
         if (currentAcdExtensionLineType !== AcdTypeEnum.SOFTPHONE && !isCurrentCallInASharedSkill && isRingtoneUsageDisabled) {
            console.debug('Starting ringtone omitted');
            return;
         }

         console.debug('Start ringtone');
         this.primaryHowls.forEach((p) => p.play());
      } else {
         console.debug('Stop ringtone');
         this.primaryHowls.forEach((p) => p.stop());
      }
   }

   public fireSignalTone(isRinging: boolean) {
      this.isRinging = isRinging;

      if (isRinging) {
         console.debug('Start fireSignalTone');
         this.secondaryHowls.forEach((p) => p.play());
      } else {
         console.debug('Stop fireSignalTone');
         this.secondaryHowls.forEach((p) => p.stop());
      }
   }

   private setupRingtonePlayers(ringerDevicesInfos: MediaDeviceInfo[]) {
      this.primaryHowls.forEach((p) => p.stop());
      this.primaryHowls.clear();

      ringerDevicesInfos.forEach((ringerDevice) => {
         const howl = this.createHowl(ringerDevice.deviceId, ringingWav.default);
         this.primaryHowls.add(howl);
      });

      ringerDevicesInfos.forEach((ringerDevice) => {
         const howl = this.createHowl(ringerDevice.deviceId, signalWav.default, true);
         this.secondaryHowls.add(howl);
      });

      // restarting ringtone required?
      if (this.isRinging) {
         this.phoneRingingChanged(true);
      }
   }

   public createHowl(deviceId: string, wave: string = ringingWav.default, ringOnce: boolean = false): Howl {
      const howl: Howl = new Howl({
         src: wave,
         // loop has a raceCondition problem with stop! Therefore it is done with a restart after howler sends the onEnd event!
         loop: false,
         volume: 1.0,
         pool: 1,
         onloaderror: (soundId, error) => {
            console.error(`loading failed (device: ${deviceId}, soundId: ${soundId}, error: ${error})`);
         },
         onplayerror: (soundId, error) => {
            console.error(`playing failed (device: ${deviceId}, soundId: ${soundId}, error: ${error})`);
         },
         onload: () => {
            console.debug(`device: ${deviceId}, ringtone loaded`);
         },
         html5: true,
      });

      // own implementation of loop!
      howl.on('end', () => {
         if (!this.isRinging || ringOnce) {
            console.debug(`Meanwhile not ringing anymore, prevent playing ringtone again`);
            return;
         }
         howl.play();
      });

      if (_.isEmpty(deviceId)) {
         return howl;
      }
      // because we have modified howler but not updated the @types we ignore the typing issue
      // @ts-ignore
      howl.setDeviceId(deviceId);

      return howl;
   }
}
